/* istanbul ignore file */
import { ContainerType } from '@lumapps/medias/types';
import { CODE_BLOCK } from '@lumapps/wrex-code-block/constants';
import { withDnD } from '@lumapps/wrex-drag-n-drop/plugin/withDnD';
import { withEmbeddedMap } from '@lumapps/wrex-embedded-map/plugin/withEmbeddedMap';
import { withEmbeddedVideo } from '@lumapps/wrex-embedded-video/plugin/withEmbeddedVideo';
import { emojiAutocompleteOption } from '@lumapps/wrex-emoji/constants';
import { withEmoji } from '@lumapps/wrex-emoji/plugin/withEmoji';
import { ALIGNED_IMAGE_WRAPPER, IMAGE_GROUP } from '@lumapps/wrex-enhanced-image/constants';
import { withEnhancedImage } from '@lumapps/wrex-enhanced-image/plugin/withEnhancedImage';
import { ImageEditor, ImageEditorOptions } from '@lumapps/wrex-enhanced-image/types';
import { isAlignedImageWrapper } from '@lumapps/wrex-enhanced-image/utils/isAlignedImageWrapper';
import { withFile } from '@lumapps/wrex-file/plugin/withFile';
import { withHTMLPaste } from '@lumapps/wrex-html-paste/plugin/withHTMLPaste';
import { HTMLPasteOptions } from '@lumapps/wrex-html-paste/types';
import { withIframe } from '@lumapps/wrex-iframe/plugin/withIframe';
import { withInlineAutocomplete } from '@lumapps/wrex-inline-autocomplete/plugin/withInlineAutocomplete';
import type { InlineAutocompleteEditor, InlineAutocompleteOptions } from '@lumapps/wrex-inline-autocomplete/types';
import { withLinkPreview } from '@lumapps/wrex-link-preview/plugin/withLinkPreview';
import { Link } from '@lumapps/wrex-link/components/blocks/Link';
import { withLink } from '@lumapps/wrex-link/plugin/withLink';
import { LinkEditor } from '@lumapps/wrex-link/types';
import { withList } from '@lumapps/wrex-list/plugin/withList';
import { ListEditor } from '@lumapps/wrex-list/types';
import { withPlayVideo } from '@lumapps/wrex-play-video/plugin/withPlayVideo';
import { QUOTE_BLOCK } from '@lumapps/wrex-quote/constants';
import { withQuote } from '@lumapps/wrex-quote/plugin/withQuote';
import { TABLE } from '@lumapps/wrex-table/constants';
import { withTable } from '@lumapps/wrex-table/plugin/withTable';
import { HEADING, PARAGRAPH } from '@lumapps/wrex-typography/constants';
import { withTypography } from '@lumapps/wrex-typography/plugin/withTypography';
import { TypographyEditor } from '@lumapps/wrex-typography/types';
import { useFetchUserMention } from '@lumapps/wrex-user-mention/hooks/useFetchUserMention';
import { withUserMention } from '@lumapps/wrex-user-mention/plugin/withUserMention';
import { UserMentionEditor, type UserMentionElement } from '@lumapps/wrex-user-mention/types';
import { createUserMention } from '@lumapps/wrex-user-mention/utils/createUserMention';
import { ReactEditor, withHistory, withReact } from '@lumapps/wrex/slate';
import { createEditor as create } from '@lumapps/wrex/slate/createEditor';
import { getHtmlPasteOptions } from '@lumapps/wrex/utils/getHtmlPasteOptions';

import { STRUCTURED_CONTENT_HTML_PASTE_OPTIONS, structuredContentEditorEnabledFeatures } from './constants';

export type SlateStructuredContentEditor = ReactEditor &
    TypographyEditor &
    ImageEditor &
    UserMentionEditor &
    InlineAutocompleteEditor &
    ListEditor &
    LinkEditor;

export const createStructuredContentEditor = ({
    uploadFunction,
    placeholder,
    htmlPasteOptions: additionalHtmlPasteOptions,
    inlineAutoCompleteParams,
    communityId,
}: {
    uploadFunction: ImageEditorOptions['uploadFunction'];
    placeholder: string;
    htmlPasteOptions?: Partial<HTMLPasteOptions>;
    isContributionAlphaEnabled?: boolean;
    inlineAutoCompleteParams?: any;
    communityId?: string;
}) => {
    const htmlPasteOptions = getHtmlPasteOptions(STRUCTURED_CONTENT_HTML_PASTE_OPTIONS);
    const config = create<SlateStructuredContentEditor>({
        placeholder,
        plugins: [
            withReact,
            withHistory,
            withDnD(),
            withHTMLPaste({
                ...htmlPasteOptions,
                ...additionalHtmlPasteOptions,
            }),
            withTypography({
                blockTypeParentMatch: {
                    [CODE_BLOCK]: isAlignedImageWrapper,
                    [PARAGRAPH]: isAlignedImageWrapper,
                },
                allowedMarkParents: [PARAGRAPH, Link.displayName, HEADING],
                allowedAlignmentParents: [PARAGRAPH, HEADING, TABLE, QUOTE_BLOCK, IMAGE_GROUP, ALIGNED_IMAGE_WRAPPER],
                allowInlinesOnlyInParagraph: true,
                enabledFeatures: structuredContentEditorEnabledFeatures.typography,
            }),
            withList({
                defaultBlockType: PARAGRAPH,
                enabledFeatures: structuredContentEditorEnabledFeatures.list,
            }),
            withLink({ allowedParentBlock: PARAGRAPH }),
            withEnhancedImage({
                uploadFunction,
                enabledFeatures: structuredContentEditorEnabledFeatures.enhancedImage,
            }),
            withEmoji(),
            withFile({ uploadFunction }),
            withQuote({ parentMatch: isAlignedImageWrapper }),
            withLinkPreview({
                uploadMediaOptions: {
                    arePublic: false,
                    // When not in a community it will fallback to the instance id (inside the useUploadMedia)
                    ...(communityId && { containerId: communityId }),
                    containerType: communityId ? ContainerType.COMMUNITY : ContainerType.SITE,
                },
            }),
            withTable(),
            withEmbeddedMap(),
            withEmbeddedVideo(),
            withPlayVideo(),
            withInlineAutocomplete([
                {
                    triggerCharacter: '@',
                    elementCreator: createUserMention,
                    getEntityId: (user) => user?.id,
                    getEntityLabel: (user) => user?.fullName,
                    getListItemProps: inlineAutoCompleteParams,
                    fetchHook: useFetchUserMention,
                } as InlineAutocompleteOptions<UserMentionElement, UserMentionElement['user']>,
                emojiAutocompleteOption,
            ]),
            withUserMention(),
            withIframe(),
        ],
    });
    return config;
};
